/**
 * CompatFlow — root component for the Compatibility Check flow.
 * Route: /flow/compat
 *
 * Instantiates useCompatFlow and switches between CC1–CC8 based on state.
 * State machine transitions:
 *   idle       → CC1Entry
 *   searching  → CC2Found (does the search, sends FOUND/NOT_FOUND)
 *   found      → CC2Found (shows partner card)
 *   not_found  → CC3Invite
 *   preview    → CC4Pair
 *   paying     → CC5Payment (overlaid on CC4Pair)
 *   computing  → CC6Computing
 *   done       → CC7Result (or CC8Share when "Share card" is tapped)
 *   error      → error screen
 */

import { useTranslation } from 'react-i18next';
import { useCompatFlow } from '../../../hooks/useCompatFlow';
import { CC1Entry } from './CC1Entry';
import { CC2Found } from './CC2Found';
import { CC3Invite } from './CC3Invite';
import { CC4Pair } from './CC4Pair';
import { CC5Payment } from './CC5Payment';
import { CC6Computing } from './CC6Computing';
import { CC7Result } from './CC7Result';
import { CC8Share } from './CC8Share';
import { CCGiftSent } from './CCGiftSent';
import styles from './Compat.module.css';

// --------------------------------------------------------------------------
// Component
// --------------------------------------------------------------------------

interface CompatFlowProps {
  /** Gift mode (gifting_crush): buy an anonymous compat reading for someone else. */
  gift?: boolean;
}

export function CompatFlow({ gift = false }: CompatFlowProps): JSX.Element {
  const flow = useCompatFlow(gift);
  const { t } = useTranslation('common');

  switch (flow.state) {
    case 'idle':
      return <CC1Entry flow={flow} />;

    case 'searching':
    case 'found':
      return <CC2Found flow={flow} />;

    case 'not_found':
      return <CC3Invite flow={flow} />;

    case 'preview':
      return <CC4Pair flow={flow} />;

    case 'paying':
      return <CC5Payment flow={flow} />;

    case 'computing':
      return <CC6Computing flow={flow} />;

    case 'done':
      return <CC7Result flow={flow} />;

    case 'sharing':
      return <CC8Share flow={flow} />;

    case 'gift_sent':
      return <CCGiftSent flow={flow} />;

    case 'error':
      return (
        <div className={styles.root}>
          <div className={styles.content}>
            <div className={styles.computingCenter} style={{ gap: 16 }}>
              <div style={{ fontSize: 32, color: 'var(--amber)' }}>⚠</div>
              <p
                style={{
                  fontFamily: 'var(--font-body)',
                  fontSize: 'var(--type-ui)',
                  color: 'var(--fg-2)',
                  textAlign: 'center',
                  maxWidth: 280,
                }}
              >
                {flow.error ?? t('errors.generic')}
              </p>
              <button
                type="button"
                className={styles.ctaPrimary}
                style={{ width: 200 }}
                onClick={() => flow.send({ type: 'RETRY' })}
              >
                {t('actions.retry')}
              </button>
            </div>
          </div>
        </div>
      );

    default:
      return <CC1Entry flow={flow} />;
  }
}

// CC8Share export for direct route usage if needed
export { CC8Share };
